import { type FC, type ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';

// components
import { PdpSharedPurchaseButton } from '../PdpSharedButtons/PdpSharedPurchaseButton.tsx';
import { PdpSharedContactDealerButton } from '../PdpSharedButtons/PdpSharedContactDealerButton.tsx';
import { PdpSharedMakeOfferButton } from '../PdpSharedButtons/PdpSharedMakeOfferButton.tsx';
import { PdpSharedShoppingButtons } from '../shared/PdpSharedShoppingButtons/PdpSharedShoppingButtons.tsx';
import { useShowPdpSharedShoppingButtons } from '../shared/PdpSharedShoppingButtons/useShowPdpSharedShoppingButtons.ts';
import { PdpSharedDelimiter } from '../shared/PdpSharedDelimiter/PdpSharedDelimiter.tsx';

// helpers
import classNames from 'classnames';
import { useDisplayInfo } from '../hooks/useDisplayInfo.ts';
import { useMultiItemCheckoutABTest } from 'dibs-checkout/exports/useMultiItemCheckoutTracking.ts';

// styles
import dibsCss from 'dibs-css';

import { type PdpRespSidebarButtons_user$key } from './__generated__/PdpRespSidebarButtons_user.graphql.ts';
import { type PdpRespSidebarButtons_item$key } from './__generated__/PdpRespSidebarButtons_item.graphql.ts';
import { type PdpRespSidebarButtons_viewer$key } from './__generated__/PdpRespSidebarButtons_viewer.graphql.ts';
import { type useDisplayInfo_displayInfo$key } from '../hooks/__generated__/useDisplayInfo_displayInfo.graphql.ts';
import { type useDisplayInfo_multiskus$key } from '../hooks/__generated__/useDisplayInfo_multiskus.graphql.ts';

type Props = {
    user: PdpRespSidebarButtons_user$key | null;
    item: PdpRespSidebarButtons_item$key;
    viewer: PdpRespSidebarButtons_viewer$key | null;
    displayInfo?: useDisplayInfo_displayInfo$key | null;
    relayVariables: {
        itemId: string;
        userId?: string | null;
        isMultiSku?: boolean;
    };
    quantitySelected?: string;
    skuId?: string;
    currency: string;
};

export const PdpRespSidebarButtons: FC<Props> = props => {
    const {
        relayVariables,
        user: userRef,
        item: itemRef,
        viewer: viewerRef,
        quantitySelected,
        skuId,
        displayInfo: displayInfoRef,
        currency,
    } = props;

    const user = useFragment(
        graphql`
            fragment PdpRespSidebarButtons_user on User {
                ...PdpSharedContactDealerButton_user
                ...PdpSharedMakeOfferButton_user
                ...PdpSharedShoppingButtons_user
            }
        `,
        userRef
    );
    const item = useFragment(
        graphql`
            fragment PdpRespSidebarButtons_item on Item {
                ...PdpSharedContactDealerButton_item
                ...PdpSharedPurchaseButton_item
                ...PdpSharedMakeOfferButton_item
                skus @include(if: $isMultiSku) {
                    ...useDisplayInfo_multiskus
                }
                ...PdpSharedShoppingButtons_item
            }
        `,
        itemRef
    );
    const viewer = useFragment(
        graphql`
            fragment PdpRespSidebarButtons_viewer on Viewer {
                ...PdpSharedShoppingButtons_viewer
                ...useShowPdpSharedShoppingButtons_viewer
            }
        `,
        viewerRef
    );
    const displayInfo = useDisplayInfo({
        displayInfo: displayInfoRef,
        skus: item.skus as useDisplayInfo_multiskus$key,
        selectedSkuId: skuId,
    });
    const { itemId, userId } = relayVariables;
    const showPdpSharedShoppingButtons = useShowPdpSharedShoppingButtons(viewer);
    const { showPurchase, showMakeOffer, showRequestPrice, showAcceptOffer } =
        displayInfo?.ctas || {};
    const isQualifyingPdp = !!showPurchase && !showRequestPrice && !showAcceptOffer;
    useMultiItemCheckoutABTest(isQualifyingPdp);

    if (!displayInfo?.ctas) {
        return null;
    }

    const buttonsClass = classNames({ [dibsCss.mtXsmall]: quantitySelected });

    const MakeOfferButton: FC = () =>
        showMakeOffer ? (
            <PdpSharedMakeOfferButton
                itemId={itemId}
                selectedSkuId={skuId}
                item={item}
                userId={userId}
                user={user}
                quantitySelected={quantitySelected}
                currency={currency}
                showArrow
                gaLabel="pdp main"
                hasPrivateOffer={showAcceptOffer}
            />
        ) : null;

    // secondary CTAs are the buttons below the title
    let secondaryCtas: ReactNode;

    if (showRequestPrice) {
        // Request price CTA is exclusive to the rest, so display it alone if showRequestPrice
        secondaryCtas = (
            <PdpSharedContactDealerButton
                type={'REQUEST_PRICE'}
                item={item}
                selectedSkuId={skuId}
                user={user}
            />
        );
    } else if (isQualifyingPdp && showPdpSharedShoppingButtons) {
        // If multi item cart is enabled, display purchase and make offer buttons
        secondaryCtas = (
            <div className={classNames(dibsCss.flex, dibsCss.flexCol, dibsCss.gapXxsmall)}>
                {showPurchase && (
                    <PdpSharedShoppingButtons
                        user={user}
                        item={item}
                        viewer={viewer}
                        quantitySelected={quantitySelected}
                        skuId={skuId}
                    />
                )}
                {showPurchase && showMakeOffer && <PdpSharedDelimiter />}
                <MakeOfferButton />
            </div>
        );
    } else {
        // Otherwise just display buttons as is
        secondaryCtas = (
            <>
                {showPurchase && (
                    <PdpSharedPurchaseButton
                        itemId={itemId}
                        item={item}
                        userId={userId}
                        quantitySelected={quantitySelected}
                        skuId={skuId}
                        hasPrivateOffer={showAcceptOffer}
                    />
                )}
                <MakeOfferButton />
            </>
        );
    }

    return (
        <div className={buttonsClass} data-tn="item-actions">
            {secondaryCtas}
        </div>
    );
};
